import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SourceProfilesList from '../requirements/SourceProfilesList';
import CandidateProfileModal from '../tracking/CandidateProfileModal';
import { searchDashboardSourceProfiles } from '../../services/sourceProfileSearchService';
import { normalizeRecruiterCandidateGroups } from '../../utils/candidateListState';

const SEARCH_DEBOUNCE_MS = 400;

const DashboardSourceProfileSearchContext = createContext(null);

function useDashboardSourceProfileSearch() {
  const value = useContext(DashboardSourceProfileSearchContext);
  if (!value) {
    throw new Error('Dashboard source profile search must be used within DashboardSourceProfileSearch.');
  }
  return value;
}

export function DashboardSourceProfileSearchBar() {
  const {
    draftQuery,
    setDraftQuery,
    loading,
    runSearch,
    handleReset,
  } = useDashboardSourceProfileSearch();

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch(draftQuery);
  };

  return (
    <form className="hr-dashboard-profile-search" onSubmit={handleSubmit}>
      <div className="hr-dashboard-profile-search-field">
        <input
          type="search"
          className="hr-dashboard-profile-search-input"
          placeholder="Search source profile name..."
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
          aria-label="Search source profile name"
        />
        {draftQuery ? (
          <button
            type="button"
            className="hr-dashboard-profile-search-clear"
            onClick={handleReset}
            disabled={loading}
            aria-label="Clear source profile search"
          >
            ×
          </button>
        ) : null}
      </div>
    </form>
  );
}

export function DashboardSourceProfileSearchResults() {
  const {
    appliedQuery,
    loading,
    loadError,
    recruitersData,
    total,
    handleViewCandidate,
    profileCandidateId,
    showProfileModal,
    setShowProfileModal,
  } = useDashboardSourceProfileSearch();

  const searchActive = Boolean(appliedQuery) || loading;
  if (!searchActive) return null;

  return (
    <>
      <section className="hr-card hr-dashboard-profile-search-results mb-3">
        {loading && (
          <p className="hr-page-hint mb-0">Searching source profiles…</p>
        )}
        {loadError && (
          <div className="alert alert-danger mb-0">{loadError}</div>
        )}
        {!loading && !loadError && total === 0 && (
          <p className="hr-empty-state mb-0">No matching source profiles found</p>
        )}
        {!loading && !loadError && total > 0 && (
          <SourceProfilesList
            recruitersData={recruitersData}
            onViewCandidate={handleViewCandidate}
            resetKey={appliedQuery}
            showClientColumn
            hideNameSearch
          />
        )}
      </section>
      <CandidateProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        candidateId={profileCandidateId}
      />
    </>
  );
}

function DashboardSourceProfileSearch({ children }) {
  const [draftQuery, setDraftQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [recruitersData, setRecruitersData] = useState([]);
  const [total, setTotal] = useState(0);
  const [profileCandidateId, setProfileCandidateId] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const requestSeq = useRef(0);

  const runSearch = useCallback(async (rawQuery) => {
    const query = String(rawQuery || '').trim();
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    if (!query) {
      setAppliedQuery('');
      setRecruitersData([]);
      setTotal(0);
      setLoadError('');
      setLoading(false);
      return;
    }

    setAppliedQuery(query);
    setLoading(true);
    setLoadError('');
    try {
      const response = await searchDashboardSourceProfiles({ q: query });
      if (requestSeq.current !== seq) return;
      setRecruitersData(normalizeRecruiterCandidateGroups(response.recruiters || []));
      setTotal(response.total ?? 0);
    } catch (error) {
      if (requestSeq.current !== seq) return;
      console.error('Source profile search failed:', error);
      setLoadError(error.message || 'Failed to search source profiles.');
      setRecruitersData([]);
      setTotal(0);
    } finally {
      if (requestSeq.current === seq) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const query = draftQuery.trim();
    if (!query) {
      requestSeq.current += 1;
      setAppliedQuery('');
      setRecruitersData([]);
      setTotal(0);
      setLoadError('');
      setLoading(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      runSearch(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draftQuery, runSearch]);

  const handleReset = useCallback(() => {
    requestSeq.current += 1;
    setDraftQuery('');
    setAppliedQuery('');
    setRecruitersData([]);
    setTotal(0);
    setLoadError('');
    setLoading(false);
    setShowProfileModal(false);
    setProfileCandidateId('');
  }, []);

  const handleViewCandidate = useCallback((candidateId) => {
    setProfileCandidateId(candidateId);
    setShowProfileModal(true);
  }, []);

  const value = useMemo(() => ({
    draftQuery,
    setDraftQuery,
    appliedQuery,
    loading,
    loadError,
    recruitersData,
    total,
    runSearch,
    handleReset,
    handleViewCandidate,
    profileCandidateId,
    showProfileModal,
    setShowProfileModal,
  }), [
    draftQuery,
    appliedQuery,
    loading,
    loadError,
    recruitersData,
    total,
    runSearch,
    handleReset,
    handleViewCandidate,
    profileCandidateId,
    showProfileModal,
  ]);

  return (
    <DashboardSourceProfileSearchContext.Provider value={value}>
      {children}
    </DashboardSourceProfileSearchContext.Provider>
  );
}

export default DashboardSourceProfileSearch;
